import { AppLayout } from '@/components/layouts/AppLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useCabasEstimate } from '@/hooks/api/useCabasEstimate'
import { toast } from '@/hooks/use-toast'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { format } from 'date-fns'
import { Check, Copy } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

const CabasSyncDetail = () => {
  const { id } = useParams()
  const { data: estimate, isLoading } = useCabasEstimate(id)
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!estimate) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(estimate, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast({ title: 'Kopierat', description: 'Rådata kopierades till urklipp.' })
    } catch {
      toast({ title: 'Kunde inte kopiera', description: 'Klipp ut texten manuellt.', variant: 'destructive' })
    }
  }

  useDocumentTitle(estimate?.CalculationId ?? estimate?.Vehicle?.VehicleIdentification)

  const registrationNumber = estimate?.Vehicle?.VehicleIdentification || '-'
  const damageDate = estimate?.Damage?.DamageDate ? format(new Date(estimate.Damage.DamageDate), 'yyyy-MM-dd') : '-'
  const updatedAt = estimate?.updatedAt ? format(new Date(estimate.updatedAt), 'yyyy-MM-dd HH:mm') : '-'
  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Synkad data från Cabas</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/cabas-sync">Tillbaka</Link>
            </Button>
            {estimate?.carId && (
              <Button asChild>
                <Link to={`/cars/${estimate.carId}`}>Visa bil</Link>
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Översikt</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : !estimate ? (
              <div className="text-muted-foreground">Ingen data hittades för detta id.</div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <DetailItem label="Calculation ID" value={estimate.CalculationId} />
                <DetailItem label="Regnr" value={registrationNumber} />
                <DetailItem label="Skadedatum" value={damageDate} />
                <DetailItem label="Senast uppdaterad" value={updatedAt} />
                <DetailItem label="Status" value={estimate.Status || '-'} />
                <DetailItem label="Work order" value={estimate.WorkOrderNumber || '-'} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Raw data</CardTitle>
            <Button variant="outline" size="sm" onClick={handleCopy} disabled={!estimate}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Kopierat' : 'Kopiera rådata'}
            </Button>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : !estimate ? (
              <div className="text-muted-foreground">Ingen data hittades för detta id.</div>
            ) : (
              <pre className="bg-muted rounded-md p-4 text-sm overflow-auto max-h-[70vh] break-all whitespace-pre-wrap">
                {JSON.stringify(estimate, null, 2)}
              </pre>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}

const DetailItem = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0">
    <div className="text-sm text-muted-foreground">{label}</div>
    <div className="text-base font-medium truncate">{value || '-'}</div>
  </div>
)

export default CabasSyncDetail
